import { useEffect, useState } from 'react'
import styles from './ScoopStatusIndicator.module.scss'

/**
 * ScoopStatusIndicator - A small status indicator badge
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} [props.icon] - Custom icon to display. If not provided, shows a simple dot
 * @param {'success' | 'error' | 'danger'} [props.variant='success'] - Color variant (success = green, error = pink, danger = red)
 * @param {number} [props.debounce=0] - Debounce delay in milliseconds before showing the indicator
 * @param {boolean} [props.visible=true] - Whether the indicator is visible
 * @param {string} [props.className] - Additional CSS classes to apply
 *
 * @example
 * // Default green connected indicator
 * <ScoopStatusIndicator />
 *
 * @example
 * // With custom icon (typing indicator)
 * <ScoopStatusIndicator icon={<DotsThreeIcon size={12} weight="bold" />} variant="error" />
 *
 * @example
 * // With debounce
 * <ScoopStatusIndicator visible={isTyping} debounce={500} icon={<DotsThreeIcon />} variant="error" />
 *
 * @example
 * // Error indicator
 * <ScoopStatusIndicator icon={<WarningCircleIcon size={12} weight="fill" />} variant="error" />
 */
export const ScoopStatusIndicator = ({
    icon = null,
    variant = 'success',
    debounce = 0,
    visible = true,
    className = '',
}) => {
    const [isVisible, setIsVisible] = useState(debounce === 0 ? visible : false)

    useEffect(() => {
        if (debounce === 0) {
            setIsVisible(visible)
            return
        }

        let timeoutId

        if (visible) {
            timeoutId = setTimeout(() => {
                setIsVisible(true)
            }, debounce)
        } else {
            setIsVisible(false)
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
        }
    }, [visible, debounce])

    if (!isVisible) {
        return null
    }

    const indicatorClassName = [
        styles.indicator,
        icon ? styles.withIcon : styles.dot,
        styles[variant],
        className,
    ]
        .filter(Boolean)
        .join(' ')

    return <div className={indicatorClassName}>{icon}</div>
}

export default ScoopStatusIndicator
